import * as React from "react";
import _ from "lodash";
import { Link, useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import SwitchPoint from "@/assets/SwitchPoint.svg";

import {
  Button,
  ButtonBase,
} from "@mui/material";
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';

import { useSelector } from '@/services/hooks';
import {
  appBarHeight,
  appBarMiniHeight,
  isAdult,
  backToMypagePath,
} from '@/constants';
import { color, mainTheme } from "@/services/themes";
import { getSession } from "@/utils.ts";
import { useMemo } from "react";

interface Props {
  title: string | React.ReactElement;
  children?: React.ReactNode;
  isPrimary?: boolean;
  hidden?: boolean;
  isBlack?: boolean;
  showSignUpButton?: boolean;
}

export default function MenuAppBar(props: Props) {
  const { title, children, isPrimary, isBlack } = props;
  const user = useSelector((state) => state.user.user);
  const navigateTo = useNavigate();
  const isConfirmed = !!getSession('age_confirm');
  const { detail } = useSelector((state) => state.user);
  const remain = _.get(detail, 'swap.item_remain', 0) + _.get(detail, 'swap.plan_remain', 0)

  const { notices } = useSelector((state) => state.common);
  const unreadCount = useMemo(() => {
    return _.filter(notices, { is_read: false }).length;
  }, [notices])

  const renderNoticeIcon = () => {
    if (!user) {
      return null;
    }
    return (
      <IconButton
        size="large"
        edge="start"
        aria-label="menu"
        sx={{
          paddingRight: '0px'
        }}
        onClick={() => {
          navigateTo('/notice')
        }}
      >
        <NotificationsIcon />
        {!!unreadCount && (
          <Box
            width="16px"
            height="16px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              borderRadius: '50%',
              backgroundColor: mainTheme.palette.primary.main,
              position: 'absolute',
              right: '-6px',
              top: '8px',
              color: 'white',
              fontSize: '11px',
            }}
          >
            {unreadCount}
          </Box>
        )}
      </IconButton>
    );
  }

  return (
    <Box>
      <AppBar
        position="fixed"
        color={isPrimary ? 'primary' : 'secondary'}
        sx={{
          display: props.hidden ? 'none' : 'block',
          backgroundColor: isPrimary ? '#FFFFFF' : color.appBarBackground,
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            component="div"
            position="relative"
            sx={{
              flexGrow: 1,
              display: isPrimary ? 'flex' : 'block',
              justifyContent: {
                md: 'center',
                sm: 'flex-start',
                xs: 'flex-start',
              },
              alignItems: 'center',
            }
            }>
            {isPrimary ? (
              <>
                <Box
                  sx={{
                    width: {
                      xs: '36px',
                    },
                  }}>
                </Box>
                <Link
                  to={`/${window.location.search}`}
                  style={{
                    display: 'contents',
                  }}
                >
                  <img
                    src={isAdult ? "/logo@2x.png" : "slack-imgs 1.png"}
                    srcSet={isAdult ? "/logo@2x.png 2x,/logo@3x.png 3x" : "/slack-imgs 1@2x.png,/slack-imgs 1@3x.png"}
                    alt="FACESWITCH"
                    style={{
                      backgroundColor: isAdult ? 'none' : 'white',
                      width: isAdult ? '116px' : '90px',
                      height: isAdult ? '37.7px' : '30px',
                    }}
                  />
                </Link>
              </>
            ) : (
              <Typography
                variant="h6"
              >
                {title}
              </Typography>
            )}
          </Typography>

          <Box
            display="flex"
            gap={1}
            alignItems="center"
            position="absolute"
            sx={{
              right: '16px'
            }}
          >
            {props.showSignUpButton ? (
              <Button
                size="small"
                variant="contained"
                sx={{ lineHeight: "22px" }}
                onClick={() => {
                  navigateTo('/users/signup')
                }}
              >
                <FormattedMessage id="header.signup" />
              </Button>
            ) : (!!isPrimary && !!isConfirmed && (
                <ButtonBase
                  onClick={() => {
                    navigateTo(backToMypagePath)
                  }}
                >
                  <Box
                    display="flex"
                    gap={1}
                    alignItems="center"
                    sx={{
                      borderRadius: '3px',
                      border: '1px solid rgba(0, 0, 0, 0.16)',
                      padding: '8px',
                      height: '36px',
                    }}
                  >
                    <img src={SwitchPoint} alt="swap icon" width="20px" />
                    <Typography
                      variant="caption"
                      color='rgba(0, 0, 0, 0.6)'
                      fontFamily='ROBOTO'
                      fontSize={16}
                      fontWeight={500}
                      lineHeight={1}
                    >
                      {remain.toLocaleString()} pt
                    </Typography>
                  </Box>
                </ButtonBase>)
            )}
            {renderNoticeIcon()}
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          position: { sm: 'absolute' },
          marginTop: {
            sm: props.hidden ? 0 : `${appBarHeight}px`,
            xs: props.hidden ? 0 : `${appBarMiniHeight}px`,
          },
          background: isBlack ? '#121212' : 'transparent',
        }}
      >
        <Box
          sx={{
            maxWidth: { sm: '600px' },
            margin: { sm: '0 auto' },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box >
  );
}
